import { useEffect, useState } from "react";
import {
    VoiceVisualizer,
    usePipecatConnectionState,
} from "@pipecat-ai/voice-ui-kit";
import { useTranslation } from "react-i18next";
import { MicIcon } from "./MicIcon";
import { WaveDots } from "./WaveDots";

function formatDuration(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
}

export function PhoneCall() {
    const { t } = useTranslation();
    const { isConnected, isConnecting, isDisconnected } =
        usePipecatConnectionState();
    const [duration, setDuration] = useState(0);

    useEffect(
        function trackCallDuration() {
            if (!isConnected) {
                setDuration(0);
                return;
            }
            const interval = window.setInterval(function tick() {
                setDuration(function increment(prev) {
                    return prev + 1;
                });
            }, 1000);
            return function cleanup() {
                window.clearInterval(interval);
            };
        },
        [isConnected],
    );

    const stateLabel = isConnected
        ? t("call.state.live")
        : isConnecting
          ? t("call.state.connecting")
          : t("call.state.idle");

    const heading = isConnected
        ? t("call.heading.connected")
        : isConnecting
          ? t("call.heading.connecting")
          : t("call.heading.ready");

    const subline = isConnected
        ? t("call.subline.connected")
        : isConnecting
          ? t("call.subline.connecting")
          : t("call.subline.idle");

    return (
        <div className="relative flex h-full min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-4 py-6">
            <div
                className="pointer-events-none absolute inset-0 -z-10 opacity-70"
                aria-hidden="true"
            >
                <div className="absolute left-1/2 top-1/3 size-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-zinc-200/60 via-zinc-100/30 to-transparent blur-3xl" />
            </div>

            <div
                className={`absolute left-1/2 top-5 inline-flex -translate-x-1/2 items-center gap-2 rounded-full border px-3.5 py-1.5 shadow-sm backdrop-blur transition-colors ${
                    isConnected
                        ? "border-emerald-200/70 bg-emerald-50/80 text-emerald-700"
                        : isConnecting
                          ? "border-amber-200/70 bg-amber-50/80 text-amber-700"
                          : "border-zinc-200/70 bg-white/70 text-zinc-500"
                }`}
            >
                <span
                    className={`size-1.5 rounded-full ${
                        isConnected
                            ? "bg-emerald-500 animate-pulse"
                            : isConnecting
                              ? "bg-amber-500 animate-pulse"
                              : "bg-zinc-400"
                    }`}
                />
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
                    {stateLabel}
                </span>
            </div>

            <div className="flex w-full max-w-md flex-col items-center gap-7 animate-slide-in">
                <div className="relative flex size-44 items-center justify-center sm:size-52">
                    <div
                        className={`absolute inset-0 rounded-full bg-zinc-300/40 ${
                            isConnected ? "animate-call-ring" : ""
                        }`}
                        aria-hidden="true"
                    />
                    <div
                        className={`absolute inset-3 rounded-full bg-zinc-300/50 ${
                            isConnected
                                ? "animate-call-ring [animation-delay:0.9s]"
                                : ""
                        }`}
                        aria-hidden="true"
                    />
                    {isConnecting && (
                        <div
                            className="absolute inset-0 rounded-full border-2 border-transparent border-t-zinc-700 border-r-zinc-700/60 animate-spin"
                            aria-hidden="true"
                        />
                    )}
                    {isDisconnected && (
                        <div
                            className="absolute inset-0 rounded-full bg-zinc-200/70 animate-pulse-ring"
                            aria-hidden="true"
                        />
                    )}

                    <div
                        className={`relative flex size-32 items-center justify-center rounded-full bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-950 text-white ring-1 ring-white/10 sm:size-36 ${
                            isConnected
                                ? "shadow-[0_24px_56px_-16px_rgba(16,16,16,0.55)]"
                                : "shadow-[0_18px_40px_-16px_rgba(16,16,16,0.4)]"
                        }`}
                    >
                        {isConnecting ? (
                            <WaveDots />
                        ) : (
                            <MicIcon className="size-9 sm:size-10" />
                        )}
                    </div>
                </div>

                <div className="space-y-1.5 text-center">
                    <h2 className="text-[22px] font-semibold tracking-tight text-zinc-900 sm:text-[24px]">
                        {t("call.callerName")}
                    </h2>
                    <p className="text-[13px] text-zinc-500">
                        {t("call.callerSubtitle")}
                    </p>
                </div>

                <div className="flex flex-col items-center gap-2">
                    <div
                        className="font-mono text-[13px] tabular-nums tracking-wider text-zinc-500"
                        dir="ltr"
                    >
                        {isConnected ? formatDuration(duration) : "00:00"}
                    </div>
                    <p className="max-w-xs text-center text-[13px] leading-relaxed text-zinc-500">
                        <span className="font-medium text-zinc-700">
                            {heading}
                        </span>
                        <span className="mx-1.5 text-zinc-300">·</span>
                        <span>{subline}</span>
                    </p>
                </div>

                <div className="flex h-9 w-full items-center justify-center">
                    {isConnected ? (
                        <VoiceVisualizer
                            participantType="bot"
                            barCount={28}
                            barGap={3}
                            barWidth={3}
                            barMaxHeight={28}
                            barColor="#52525b"
                            backgroundColor="transparent"
                            barOrigin="center"
                            className="h-full"
                        />
                    ) : (
                        <div
                            className="h-px w-24 bg-gradient-to-r from-transparent via-zinc-300 to-transparent"
                            aria-hidden="true"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
